/**
 * One-shot entrance animations for shapes that arrive from elsewhere (an agent
 * turn, a collaborator). Callers queue an entrance before the shape mounts; the
 * shape view takes it on mount and plays it before the first paint, so the
 * shape never flashes in fully drawn.
 *
 * Animations use opacity and the individual `translate`/`scale`/`filter`
 * properties, which compose with the view's inline `transform` and clip.
 */
export type EntranceKind = "sheet" | "text" | "shape" | "stroke" | "image" | "scene";
export type Entrance = { kind: EntranceKind; delay: number; queued: number };

const queue = new Map<string, Entrance>();
/** 3D models that just arrived build themselves once, inside the shape. */
const freshScenes = new Map<string, number>();
/** Models an edit just changed: only these elements rebuild, once. */
const changedScenes = new Map<string, { ids: string[]; at: number }>();
/** An entrance not played within this window (the shape stayed offscreen) is dropped. */
export const ENTRANCE_TTL_MS = 6000;

export function queueEntrance(id: string, kind: EntranceKind, delay: number) {
  queue.set(id, { kind, delay, queued: Date.now() });
  if (kind === "scene") freshScenes.set(id, Date.now());
}

/** Whether a 3D model just arrived and should play its build (once). */
export function takeFreshScene(id: string, now = Date.now()) {
  const at = freshScenes.get(id);
  freshScenes.delete(id);
  return at !== undefined && now - at < ENTRANCE_TTL_MS;
}

export function queueSceneChange(id: string, elementIds: string[]) {
  if (elementIds.length) changedScenes.set(id, { ids: elementIds, at: Date.now() });
}

/** The elements a just-arrived edit changed, if it is still fresh. `take` consumes it. */
export function sceneChange(id: string, take = false, now = Date.now()) {
  const change = changedScenes.get(id);
  if (take) changedScenes.delete(id);
  return change && now - change.at < ENTRANCE_TTL_MS ? change.ids : undefined;
}

export function takeEntrance(id: string, now = Date.now()) {
  const entrance = queue.get(id);
  if (!entrance) return undefined;
  queue.delete(id);
  return now - entrance.queued > ENTRANCE_TTL_MS ? undefined : entrance;
}

export function clearEntrances() {
  queue.clear();
  freshScenes.clear();
  changedScenes.clear();
}

const EASE_OUT = "cubic-bezier(.16,.84,.3,1)";

/** Play an entrance on a mounted `.canvas-shape` element. */
export function playEntrance(element: HTMLElement, entrance: Entrance) {
  if (typeof element.animate !== "function") return;
  const { kind } = entrance;
  // A shape mounted late (scrolled into view) keeps a short delay only.
  const late = Date.now() - entrance.queued > 1500;
  const delay = late ? 0 : entrance.delay;
  const base = { delay, easing: EASE_OUT, fill: "backwards" as const };
  if (kind === "scene") {
    // The model builds itself inside; the frame only fades in.
    element.animate([{ opacity: 0 }, { opacity: 1 }], { ...base, duration: 320 });
  } else if (kind === "sheet") {
    element.animate(
      [
        { opacity: 0, scale: "0.985", translate: "0 10px" },
        { opacity: 1, scale: "1", translate: "0 0" },
      ],
      { ...base, duration: 460 },
    );
  } else if (kind === "text") {
    element.animate(
      [
        { opacity: 0, translate: "0 8px", filter: "blur(2px)" },
        { opacity: 1, translate: "0 0", filter: "blur(0)" },
      ],
      { ...base, duration: 380 },
    );
  } else if (kind === "image") {
    element.animate(
      [
        { opacity: 0, scale: "1.025", filter: "blur(10px) saturate(.6)" },
        { opacity: 1, scale: "1", filter: "blur(0) saturate(1)" },
      ],
      { ...base, duration: 640 },
    );
  } else if (kind === "stroke") {
    const drawn = drawOn(element, delay);
    element.animate([{ opacity: 0 }, { opacity: 1 }], {
      ...base,
      duration: drawn ? 120 : 360,
    });
  } else {
    element.animate(
      [
        { opacity: 0, scale: "0.94" },
        { opacity: 1, scale: "1" },
      ],
      { ...base, duration: 400 },
    );
  }
}

/** Draw stroked SVG paths on from their start. Returns false when nothing could be measured. */
function drawOn(element: HTMLElement, delay: number) {
  const paths = Array.from(
    element.querySelectorAll<SVGGeometryElement>("path, line, polyline, polygon"),
  ).slice(0, 40);
  let drawn = false;
  for (const path of paths) {
    let length = 0;
    try {
      length = path.getTotalLength();
    } catch {
      continue;
    }
    if (!Number.isFinite(length) || length < 1) continue;
    drawn = true;
    path.animate(
      [
        { strokeDasharray: `${length}`, strokeDashoffset: `${length}` },
        { strokeDasharray: `${length}`, strokeDashoffset: "0" },
      ],
      {
        delay,
        duration: Math.min(900, 360 + length * 0.6),
        easing: "cubic-bezier(.45,.05,.25,1)",
        fill: "backwards",
      },
    );
  }
  return drawn;
}
